import React, { useCallback, useContext, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { MultipleSelect } from './MultipleSelect';
import style from './RestaurantMultipleSelect.module.scss';

interface IProps {
	setAdditionalRestaurants: (value: string[]) => void;
	selectedAdditionalRestaurants: string[];
	fetchedRestaurants: IItemForGetShortInfo[];
	IDSelectedRestaurant: Nullable<string>;
}

export const RestaurantMultipleSelect: React.FC<IProps> = ({
	setAdditionalRestaurants,
	selectedAdditionalRestaurants,
	fetchedRestaurants,
	IDSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const additionalRestaurants = useMemo(() => {
		const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
		const restaurantsFromServer = fetchedRestaurants.filter((el) => el.id !== IDSelectedRestaurant) || [];
		const intersectedRestaurants = restaurantsFromServer.filter((el) => accessibleRestaurantIDs.includes(el.id));
		return hasAccess ? restaurantsFromServer : intersectedRestaurants;
	}, [fetchedRestaurants, loggedUser, hasAccess, IDSelectedRestaurant]);

	const handleRemove = useCallback(
		(id: string) => {
			const selectedItems = selectedAdditionalRestaurants.filter((el) => el !== id);
			setAdditionalRestaurants(selectedItems);
		},
		[setAdditionalRestaurants, selectedAdditionalRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return selectedAdditionalRestaurants.map((el) => {
			const label = additionalRestaurants.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, selectedAdditionalRestaurants, additionalRestaurants]);

	return (
		<div className={style.container} data-test="RestaurantField">
			<MultipleSelect
				data={additionalRestaurants}
				setAdditionalRestaurants={setAdditionalRestaurants}
				selectedAdditionalRestaurants={selectedAdditionalRestaurants}
			/>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};
